import { Box, Button, Icon, IconButton, Link, Text, VisuallyHidden, useToast } from "@chakra-ui/react";
import { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FiHelpCircle } from "react-icons/fi";
import { useIntercom } from "react-use-intercom";
import { Tooltip } from "../../components/Tooltip";
import { useIntercomUnreadCount } from "./intercomUnread";

export const HelpButton = ({ compact = false }: { compact?: boolean }) => {
  const { t } = useTranslation();
  const openHelp = useOpenHelp();
  const unread = useIntercomUnreadCount();
  const unreadId = useId();
  const badge = unread > 0 && (
    <>
      <VisuallyHidden id={unreadId}>{t("{{count}} unread", { count: unread })}</VisuallyHidden>
      <Box
        aria-hidden
        minW="18px"
        h="18px"
        px="1"
        rounded="full"
        bg="smAlertBadge"
        color="white"
        fontSize="2xs"
        fontWeight="bold"
        display="flex"
        alignItems="center"
        justifyContent="center"
        pointerEvents="none"
        {...(compact
          ? { position: "absolute", top: "0", right: "0", border: "2px solid", borderColor: "smBackground" }
          : { ml: "auto" })}
      >
        {unread > 9 ? "9+" : unread}
      </Box>
    </>
  );

  if (compact) {
    return (
      <Box position="relative" display="inline-flex">
        <Tooltip label={t("Help")} placement="right">
          <IconButton
            variant="ghost"
            size="sm"
            color="smMuted"
            aria-label={t("Help")}
            aria-describedby={unread > 0 ? unreadId : undefined}
            icon={<Icon as={FiHelpCircle} />}
            onClick={openHelp}
          />
        </Tooltip>
        {badge}
      </Box>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      w="full"
      justifyContent="flex-start"
      color="smMuted"
      fontWeight="normal"
      leftIcon={<Icon as={FiHelpCircle} />}
      aria-describedby={unread > 0 ? unreadId : undefined}
      onClick={openHelp}
    >
      {t("Help")}
      {badge}
    </Button>
  );
};

const SUPPORT_EMAIL = "support@sourcemagnet.com";
const MESSENGER_OPEN_GRACE_MS = 2500;

const useOpenHelp = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { show, isOpen } = useIntercom();
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const pending = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(pending.current), []);

  return () => {
    show();
    clearTimeout(pending.current);
    pending.current = setTimeout(() => {
      if (isOpenRef.current) return;
      toast({
        id: "help-unavailable",
        status: "warning",
        isClosable: true,
        duration: null,
        title: t("The help chat could not open"),
        description: (
          <Text>
            {t("It may be blocked by your browser or your network. You can also write to us at")}{" "}
            <Link href={`mailto:${SUPPORT_EMAIL}`} textDecoration="underline">
              {SUPPORT_EMAIL}
            </Link>
            .
          </Text>
        ),
      });
    }, MESSENGER_OPEN_GRACE_MS);
  };
};
